/**
 * Green Flag — EventCard
 *
 * Horizontal event tile used in the "EXPLORE UPCOMING EVENTS" list.
 * Left: 116×116 image. Right: title / date (eyebrow) / place / ages + optional
 * Sparks chip.
 */

import { Box, HStack, chakra, useSlotRecipe } from "@chakra-ui/react";
import { ReactNode } from "react";
import { SparksChip } from "../SparksChip/SparksChip";

export interface EventCardProps {
  date: string;
  title: string;
  place?: string;
  ages?: string;
  img: string;
  sparks?: number;
  /** Attendee photo urls shown as overlapping circles inside the chip. */
  sparkAvatars?: string[];
  /** Prepend a lock circle to the chip's avatar stack. */
  sparksLocked?: boolean;
  /** Additional chip nodes (e.g. match counts on past events). */
  extraChips?: ReactNode;
  /** The spark this card is for. Used only to tag a stable test id. */
  sparkId?: number;
  onClick?: () => void;
}

export function EventCard({
  date,
  title,
  place,
  ages,
  img,
  sparks,
  sparkAvatars,
  sparksLocked,
  extraChips,
  sparkId,
  onClick,
}: EventCardProps) {
  const recipe = useSlotRecipe({ key: "eventCard" });
  const styles = recipe({});

  return (
    <chakra.div
      css={styles.root}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      data-testid={sparkId != null ? `event-card-${sparkId}` : undefined}
    >
      <Box css={styles.image} style={{ backgroundImage: `url(${img})` }} />
      <Box css={styles.body}>
        <chakra.div css={styles.title}>{title}</chakra.div>
        <chakra.div css={styles.date}>{date}</chakra.div>
        {place && <chakra.div css={styles.meta}>{place}</chakra.div>}
        {ages && <chakra.div css={styles.meta}>{ages}</chakra.div>}
        {sparks != null && (
          <chakra.div css={styles.sparks}>
            <SparksChip
              count={sparks}
              avatars={sparkAvatars}
              locked={sparksLocked}
            />
          </chakra.div>
        )}
        {extraChips && (
          <HStack gap="2" mt="2" flexWrap="wrap">
            {extraChips}
          </HStack>
        )}
      </Box>
    </chakra.div>
  );
}
